import { Button, Grid, TextField, MenuItem, FormControlLabel, Switch, DialogActions, Box, Typography, IconButton } from '@mui/material';
import React, { useState, useContext, useEffect } from 'react';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import CustomerService from 'services/CustomerService';
import { AlertMessagesContext } from 'react-alert-messages';
import { ThemeContext } from 'Context/ThemeContext';
import Loader from 'components/utils/Loading';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';

export default function NewCustomerDialog(props) {
  const TYPE = {
    CREDIT: 'Credit',
    ADVANCE: 'Advance',
  };

  const { closeCreateNewCustomerDialog, reloadCustomers, customerGroups } = props;
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { theme } = useContext(ThemeContext);
  const { shop } = useContext(ShopContext);
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [location, setLocation] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [creditLimit, setCreditLimit] = useState(-500);
  const [trn, setTrn] = useState('');
  const [code, setCode] = useState('');
  const [group, setGroup] = useState('none');
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [creditType, setCreditType] = useState(TYPE.CREDIT);
  const [balanceType, setBalanceType] = useState(TYPE.CREDIT);
  const [amount, setAmount] = useState(0);
  const [order, setOrder] = useState(null);
  const [enableLoyaltyPoints, setEnableLoyaltyPoints] = useState(false);

  const createNewCustomer = async () => {
    const amt = Number(balanceAmount) || 0;
    const adjustedBalance = balanceType === TYPE.CREDIT ? -Math.abs(amt) : Math.abs(amt);
    const data = {
      name,
      mobile,
      location,
      credit_limit: creditLimit,
      trn_number: trn,
      group_id: group === 'none' ? null : group,
      balance: adjustedBalance,
      code,
      order: order ? Number(order) : null,
      enable_loyalty_points: enableLoyaltyPoints,
    };
    setLoadingIndicator(true);
    try {
      const resp = await CustomerService.createCustomer(data);
      postAlertMessage({
        text: tPopup('Customer created successfully'),
        type: 'success',
      });
      if (reloadCustomers) {
        reloadCustomers(resp);
      }
      closeCreateNewCustomerDialog();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  useEffect(() => {
    if (amount !== '') {
      setCreditLimit(creditType === TYPE.CREDIT ? -1 * amount : amount);
    } else {
      setCreditLimit('');
    }
  }, [creditType, amount, TYPE.CREDIT]);

  return (
    <Box className={styles.popUp}>
      <Box className={styles.contentWrapper}>
        <Loader isOpen={loadingIndicator} />
        <Box className={styles.headSec}>
          <Typography component="h2" className={styles.editTitle}>{tCommon('Create New Customer')}</Typography>
          <IconButton onClick={closeCreateNewCustomerDialog} sx={{ position: 'absolute', top: 12, right: 12 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={styles.inputSec}>
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Customer Name')}
                variant="outlined"
                size="small"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Customer Code')}
                variant="outlined"
                size="small"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
              />
            </Grid>

            {/* Row 2: Contact */}
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Customer Mobile')}
                name="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Customer Place')}
                variant="outlined"
                size="small"
                name="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
              />
            </Grid>

            {/* Row 3: Classification */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Customer Group')}
                variant="outlined"
                size="small"
                value={group}
                onChange={(event) => setGroup(event.target.value)}
                fullWidth
              >
                <MenuItem value="none">None</MenuItem>
                {customerGroups &&
                  customerGroups.map((cg) => (
                    <MenuItem value={cg.id} key={cg.id}>
                      {cg.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={theme?.variable?.trn_label}
                variant="outlined"
                size="small"
                value={trn}
                onChange={(e) => setTrn(e.target.value.toUpperCase())}
                fullWidth
              />
            </Grid>

            {/* Row 4: Financials */}
            <Grid item xs={12} sm={6} container spacing={1} alignItems="center">
              <Grid item xs={4}>
                <TextField
                  select
                  label={tCommon('Credit Type')}
                  variant="outlined"
                  size="small"
                  value={creditType}
                  onChange={(e) => setCreditType(e.target.value)}
                  className={styles.selectBox}
                  fullWidth
                >
                  <MenuItem value={TYPE.CREDIT}>{tCommon('Credit')}</MenuItem>
                  <MenuItem value={TYPE.ADVANCE}>{tCommon('Advance')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8}>
                <NumberField
                  label={tCommon('Amount')}
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setAmount('');
                    } else {
                      const parsed = parseFloat(value);
                      const formatted = ShopsHelper.getAmountFormatted(shop, parsed);
                      setAmount(formatted);
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} container spacing={1} alignItems="center">
              <Grid item xs={4}>
                <TextField
                  select
                  label={tCommon('Balance Type')}
                  variant="outlined"
                  size="small"
                  value={balanceType}
                  onChange={(e) => setBalanceType(e.target.value)}
                  className={styles.selectBox}
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': {
                      color: balanceType === TYPE.CREDIT ? 'red' : 'green',
                    },
                  }}
                >
                  <MenuItem value={TYPE.CREDIT}>{tCommon('Credit')}</MenuItem>
                  <MenuItem value={TYPE.ADVANCE}>{tCommon('Advance')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8}>
                <NumberField
                  label={tCommon('Balance')}
                  value={balanceAmount}
                  onChange={(e) => {
                    const val = e.target.value;

                    if (val === '') {
                      setBalanceAmount('');
                    } else {
                      const parsed = parseFloat(val);
                      const formatted = ShopsHelper.getAmountFormatted(shop, parsed);
                      setBalanceAmount(formatted);
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Row 5: Other */}
            <Grid item xs={12} sm={6}>
              <NumberField label={tCommon('Sort Order')} value={order} onChange={(e) => setOrder(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableLoyaltyPoints}
                    onChange={(e) => setEnableLoyaltyPoints(e.target.checked)}
                    color="primary"
                  />
                }
                label={tCommon('Enable Loyalty Points')}
              />
            </Grid>
          </Grid>
        </Box >
        <Box className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={closeCreateNewCustomerDialog}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              sx={{ backgroundColor: '#00a65a' }}
              disabled={name === '' || mobile === '' || location === '' || loadingIndicator}
              onClick={createNewCustomer}
            >
              {tCommon('Create Customer')}
            </Button>
          </DialogActions>
        </Box>
      </Box>
    </Box>
  );
}
